// GAVION gavion-context: the « Contexte Gavion » (configuration gavion.ai.company_context) and its trusted facts
// (gavion.ai.company_facts). Every AI email — the follow-up touches (Sonnet) and the drafts (« Rédiger avec l'IA »,
// R20–R23, Haiku) — reads them in their own cached system block, before the rep's stable block, as Gavion's own verified
// information (never the prospect's website, which stays untrusted). The facts also reach the guard (DraftPrompt.trustedFacts): a
// draft may state a figure about Gavion only when a fact (or the catalog, or the lead's own context) states it, and a
// promo word only in a fact's own form (guard.ts factAllows). Admins edit
// both in Paramètres → IA; every signed-in user reads the context in Formation → « Tout sur Gavion ». While a key is
// unset the default applies (the SQL seed 73_ stores the same default). Pure.
import {
  DEFAULT_COMPANY_CONTEXT,
  DEFAULT_COMPANY_FACTS,
  type CompanyFact,
} from "./companyContextDefault.ts";
import type { Lang } from "./types.ts";

export { DEFAULT_COMPANY_CONTEXT, DEFAULT_COMPANY_FACTS, type CompanyFact };

export const COMPANY_CONTEXT_MAX_CHARS = 30_000;
export const COMPANY_FACTS_MAX = 150;
export const COMPANY_FACT_TEXT_MAX = 500;
export const COMPANY_FACT_SOURCE_MAX = 300;

type Obj = Record<string, unknown>;
const aiOf = (ai: unknown): Obj =>
  typeof ai === "object" && ai !== null && !Array.isArray(ai)
    ? (ai as Obj)
    : {};
const cut = (s: string, max: number): string =>
  Array.from(s).slice(0, max).join("");

/** gavion.ai.company_context when it is non-empty text (cut at COMPANY_CONTEXT_MAX_CHARS), else the default. */
export function companyContextFromAi(ai: unknown): string {
  const v = aiOf(ai).company_context;
  const s = typeof v === "string" ? v.trim() : "";
  return s ? cut(s, COMPANY_CONTEXT_MAX_CHARS) : DEFAULT_COMPANY_CONTEXT;
}

/**
 * gavion.ai.company_facts when it is an array (an admin may keep none): its entries with a non-empty text, cut to their
 * limits, at most COMPANY_FACTS_MAX; anything else (unset, not an array) is the default list.
 */
export function companyFactsFromAi(ai: unknown): CompanyFact[] {
  const v = aiOf(ai).company_facts;
  if (!Array.isArray(v)) return DEFAULT_COMPANY_FACTS.map((f) => ({ ...f }));
  const out: CompanyFact[] = [];
  for (const e of v) {
    const o = aiOf(e);
    const text =
      typeof o.text === "string" ? o.text.replace(/\s+/g, " ").trim() : "";
    if (!text) continue;
    const source =
      typeof o.source === "string" ? o.source.replace(/\s+/g, " ").trim() : "";
    out.push({
      text: cut(text, COMPANY_FACT_TEXT_MAX),
      source: cut(source, COMPANY_FACT_SOURCE_MAX),
    });
    if (out.length >= COMPANY_FACTS_MAX) break;
  }
  return out;
}

const T = {
  fr: {
    title:
      "## Contexte Gavion (information officielle et vérifiée de Gavion, à utiliser comme faits)",
    facts:
      "## Faits vérifiés sur Gavion (les seuls chiffres sur Gavion que tu peux citer, avec ceux de l'offre)",
  },
  en: {
    title:
      "## Gavion context (Gavion's official, verified information, to use as facts)",
    facts:
      "## Verified facts about Gavion (the only figures about Gavion you may state, with the offer's)",
  },
} as const;

/** The trusted Gavion block of the system prompt (its own cache breakpoint): the context, then the facts. */
export function renderCompanyBlock(
  context: string,
  facts: readonly CompanyFact[],
  lang: Lang,
): string {
  const t = T[lang];
  const out = [
    t.title,
    "<contexte_gavion>",
    context.trim(),
    "</contexte_gavion>",
  ];
  if (facts.length > 0) out.push(t.facts, ...facts.map((f) => `- ${f.text}`));
  return out.join("\n");
}
